import { useEffect, useRef } from "react";

// Audit V1: YouTube player wrapper that uses the IFrame Player API so the
// host page can read currentTime / duration and fire heartbeats +
// milestones on the same cadence as the Mux player.
//
// The official YouTube IFrame API loads from
// https://www.youtube.com/iframe_api and posts a global onYouTubeIframeAPIReady
// once ready. We guard against multiple loads since the watch page may
// remount when the active item changes.

declare global {
  interface Window {
    YT?: YouTubeNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

interface YouTubeNamespace {
  Player: new (
    element: HTMLElement | string,
    opts: {
      videoId?: string;
      playerVars?: Record<string, unknown>;
      events?: {
        onReady?: (e: { target: YouTubePlayer }) => void;
        onStateChange?: (e: { data: number; target: YouTubePlayer }) => void;
      };
    },
  ) => YouTubePlayer;
  PlayerState: {
    UNSTARTED: number;
    ENDED: number;
    PLAYING: number;
    PAUSED: number;
    BUFFERING: number;
    CUED: number;
  };
}

interface YouTubePlayer {
  getCurrentTime(): number;
  getDuration(): number;
  destroy(): void;
}

let apiLoading: Promise<YouTubeNamespace> | null = null;

function loadIframeApi(): Promise<YouTubeNamespace> {
  if (typeof window === "undefined") return Promise.reject(new Error("SSR"));
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (apiLoading) return apiLoading;
  apiLoading = new Promise((resolve) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      if (window.YT) resolve(window.YT);
    };
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(tag);
  });
  return apiLoading;
}

interface YoutubeEmbedProps {
  videoId: string;
  title: string;
  onProgress: (currentTimeSeconds: number, durationSeconds: number) => void;
  // Fired on the IFrame API's ENDED state — the authoritative "watched to the
  // end" signal. The 1s poll alone missed short videos whose reachable
  // currentTime never crosses the (approximate) getDuration() threshold.
  onEnded?: (durationSeconds: number) => void;
}

export function YoutubeEmbed({ videoId, title, onProgress, onEnded }: YoutubeEmbedProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<YouTubePlayer | null>(null);
  const pollRef = useRef<number | null>(null);
  const progressRef = useRef(onProgress);
  const endedRef = useRef(onEnded);
  // Latest callbacks without re-creating the player on each re-render.
  progressRef.current = onProgress;
  endedRef.current = onEnded;

  useEffect(() => {
    let disposed = false;
    let cleanupContainer: HTMLDivElement | null = null;
    (async () => {
      const YT = await loadIframeApi();
      if (disposed || !containerRef.current) return;
      const innerHost = document.createElement("div");
      containerRef.current.appendChild(innerHost);
      cleanupContainer = innerHost;
      playerRef.current = new YT.Player(innerHost, {
        videoId,
        playerVars: {
          rel: 0,
          modestbranding: 1,
          autoplay: 1,
          playsinline: 1,
          // YouTube recommends passing the embedding origin to the IFrame API;
          // its absence is a known cause of "configuration"/153 errors in some
          // browsers (notably Safari). Videos whose owner disabled embedding
          // will still show YouTube's own error — that's not ours to override.
          origin: typeof window !== "undefined" ? window.location.origin : undefined,
          enablejsapi: 1,
        },
        events: {
          onReady: () => {
            // Poll progress every 1s while playing.
            pollRef.current = window.setInterval(() => {
              const p = playerRef.current;
              if (!p) return;
              try {
                const t = p.getCurrentTime();
                const d = p.getDuration();
                if (d > 0 && t >= 0) progressRef.current(t, d);
              } catch {
                /* not ready yet */
              }
            }, 1000);
          },
          onStateChange: (e) => {
            if (e.data === YT.PlayerState.ENDED) {
              try {
                endedRef.current?.(playerRef.current?.getDuration() ?? 0);
              } catch {
                /* player already torn down */
              }
            }
          },
        },
      });
    })();
    return () => {
      disposed = true;
      if (pollRef.current) window.clearInterval(pollRef.current);
      pollRef.current = null;
      try {
        playerRef.current?.destroy();
      } catch {
        /* already gone */
      }
      playerRef.current = null;
      if (cleanupContainer && cleanupContainer.parentElement) {
        cleanupContainer.parentElement.removeChild(cleanupContainer);
      }
    };
  }, [videoId]);

  return (
    <div
      ref={containerRef}
      className="w-full h-full"
      data-title={title}
      aria-label={title}
    />
  );
}
